import React from "react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ReferenceArea, Cell, LabelList,
} from "recharts";
import { useFetch } from "@/lib/api";
import { PageHeader, Panel, StatusChip, LoadingState, DemoBadge } from "@/components/app/primitives";
import { LiveAnalyticsPanel } from "@/components/app/liveModulePanels";

const GRID = "rgba(233,238,245,0.08)";
const AXIS = "#7F8A99";
const TT_STYLE = { backgroundColor: "#11161D", border: "1px solid #243041", borderRadius: 8, color: "#E9EEF5", fontSize: 12 };

const ChartCard = ({ title, note, children, testId, tall }) => (
  <Panel title={title} testId={testId} right={<DemoBadge />}>
    <div className={tall ? "h-64" : "h-52"}>{children}</div>
    {note && <p className="text-[11px] text-[--tx-3] leading-relaxed mt-2.5 border-t border-[--divider] pt-2">{note}</p>}
  </Panel>
);

export default function AnalyticsPage() {
  const { data: a, loading } = useFetch("/analytics");
  if (loading || !a) return <LoadingState message="Building analytics workspace..." />;

  return (
    <div data-testid="analytics-page">
      <PageHeader
        kicker="Scene 5 - Data analytics workspace"
        title="Analytics Workspace"
        subtitle="Order Cancellation Rate drivers across timing, channel, market, vendor, permits, save execution, and fallout. Every cut is labeled illustrative until the validated extract lands - the fields panel below lists exactly what closes that gap."
      />

      <LiveAnalyticsPanel />

      {/* Danger zone - the marquee chart */}
      <Panel title={a.timing_curve.title} className="mb-4" testId="analytics-danger-zone-chart"
        right={<div className="flex items-center gap-2"><StatusChip tone="risk">Danger zone: day {a.timing_curve.danger_zone.start}-{a.timing_curve.danger_zone.end}</StatusChip><DemoBadge /></div>}>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={a.timing_curve.data} margin={{ top: 8, right: 12, left: -14, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="day" stroke={AXIS} tick={{ fontSize: 11 }} label={{ value: "Days since order placement", position: "insideBottom", offset: -2, fontSize: 10, fill: AXIS }} />
              <YAxis stroke={AXIS} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={TT_STYLE} labelFormatter={(d) => `Day ${d}`} />
              <ReferenceArea x1={8} x2={14} fill="rgba(211,155,42,0.14)" stroke="rgba(211,155,42,0.35)" />
              <Line type="monotone" dataKey="cancels" stroke="#2AA7A1" strokeWidth={2.5} dot={false} name="Cancels" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-[--tx-3] leading-relaxed mt-2.5 border-t border-[--divider] pt-2">{a.timing_curve.note}</p>
      </Panel>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <ChartCard title={a.monthly_trend.title} note={a.monthly_trend.note} testId="analytics-monthly-trend">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={a.monthly_trend.data} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="month" stroke={AXIS} tick={{ fontSize: 10 }} interval={1} />
              <YAxis stroke={AXIS} tick={{ fontSize: 11 }} domain={[12, 21]} />
              <Tooltip contentStyle={TT_STYLE} />
              <ReferenceLine y={a.monthly_trend.target} stroke="#2EAD6B" strokeDasharray="5 4" label={{ value: "Target 15%", fill: "#2EAD6B", fontSize: 10, position: "insideBottomRight" }} />
              <ReferenceLine y={a.monthly_trend.q3_ambition} stroke="#D39B2A" strokeDasharray="3 4" label={{ value: "Q3 15.5%", fill: "#D39B2A", fontSize: 10, position: "insideTopRight" }} />
              <Line type="monotone" dataKey="rate" stroke="#E9EEF5" strokeWidth={2} dot={{ r: 2.5, fill: "#E9EEF5" }} name="OCR %" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={a.scheduled_vs_unscheduled.title} note={a.scheduled_vs_unscheduled.note} testId="analytics-scheduled-split">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={a.scheduled_vs_unscheduled.data} layout="vertical" margin={{ top: 8, right: 44, left: 30, bottom: 0 }}>
              <CartesianGrid stroke={GRID} horizontal={false} />
              <XAxis type="number" stroke={AXIS} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="cohort" stroke={AXIS} tick={{ fontSize: 11 }} width={130} />
              <Tooltip contentStyle={TT_STYLE} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="rate" name="Cancel rate %" radius={[0, 4, 4, 0]} barSize={26}>
                {a.scheduled_vs_unscheduled.data.map((d, i) => <Cell key={d.cohort} fill={i === 1 ? "#D14B4B" : "#2EAD6B"} />)}
                <LabelList dataKey="rate" position="right" formatter={(v) => `${v}%`} style={{ fill: "#E9EEF5", fontSize: 11 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={a.by_channel.title} note={a.by_channel.note} testId="analytics-by-channel">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={a.by_channel.data} margin={{ top: 14, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="channel" stroke={AXIS} tick={{ fontSize: 9 }} interval={0} tickFormatter={(v) => v.split(" (")[0].slice(0, 14)} />
              <YAxis stroke={AXIS} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={TT_STYLE} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="rate" name="OCR %" radius={[4, 4, 0, 0]} barSize={34}>
                {a.by_channel.data.map((d) => <Cell key={d.channel} fill={d.rate > 20 ? "#D39B2A" : "#2AA7A1"} />)}
                <LabelList dataKey="rate" position="top" formatter={(v) => `${v}%`} style={{ fill: "#B7C0CC", fontSize: 10 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={a.by_market.title} note={a.by_market.note} testId="analytics-by-market" >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={a.by_market.data} margin={{ top: 14, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="market" stroke={AXIS} tick={{ fontSize: 9 }} interval={0} tickFormatter={(v) => v.split(" ")[0]} />
              <YAxis stroke={AXIS} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={TT_STYLE} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="rate" name="OCR %" radius={[4, 4, 0, 0]} barSize={22}>
                {a.by_market.data.map((d) => <Cell key={d.market} fill={d.rate > 20 ? "#D14B4B" : d.rate > 16 ? "#D39B2A" : "#2EAD6B"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={a.by_vendor.title} note={a.by_vendor.note} testId="analytics-by-vendor">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={a.by_vendor.data} layout="vertical" margin={{ top: 4, right: 44, left: 50, bottom: 0 }}>
              <CartesianGrid stroke={GRID} horizontal={false} />
              <XAxis type="number" stroke={AXIS} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="vendor" stroke={AXIS} tick={{ fontSize: 10 }} width={140} />
              <Tooltip contentStyle={TT_STYLE} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="rate" name="OCR %" radius={[0, 4, 4, 0]} barSize={16}>
                {a.by_vendor.data.map((d) => <Cell key={d.vendor} fill={d.rate > 20 ? "#D14B4B" : d.rate > 15 ? "#D39B2A" : "#2EAD6B"} />)}
                <LabelList dataKey="rate" position="right" formatter={(v) => `${v}%`} style={{ fill: "#E9EEF5", fontSize: 10 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={a.reschedule_impact.title} note={a.reschedule_impact.note} testId="analytics-reschedule">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={a.reschedule_impact.data} margin={{ top: 14, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="reschedules" stroke={AXIS} tick={{ fontSize: 11 }} label={{ value: "Reschedule count", position: "insideBottom", offset: -2, fontSize: 10, fill: AXIS }} />
              <YAxis stroke={AXIS} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={TT_STYLE} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="rate" name="Cancel rate %" radius={[4, 4, 0, 0]} barSize={38}>
                {a.reschedule_impact.data.map((d, i) => <Cell key={d.reschedules} fill={["#2AA7A1", "#D39B2A", "#D14B4B", "#B01212"][i]} />)}
                <LabelList dataKey="rate" position="top" formatter={(v) => `${v}%`} style={{ fill: "#B7C0CC", fontSize: 10 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={a.save_timing.title} note={a.save_timing.note} testId="analytics-save-timing">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={a.save_timing.data} layout="vertical" margin={{ top: 4, right: 44, left: 60, bottom: 0 }}>
              <CartesianGrid stroke={GRID} horizontal={false} />
              <XAxis type="number" stroke={AXIS} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="timing" stroke={AXIS} tick={{ fontSize: 10 }} width={150} />
              <Tooltip contentStyle={TT_STYLE} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="conversion" name="Save conversion %" radius={[0, 4, 4, 0]} barSize={18}>
                {a.save_timing.data.map((d, i) => <Cell key={d.timing} fill={["#2EAD6B", "#2AA7A1", "#D39B2A", "#D14B4B"][i]} />)}
                <LabelList dataKey="conversion" position="right" formatter={(v) => `${v}%`} style={{ fill: "#E9EEF5", fontSize: 10 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={a.fallout_pareto.title} note={a.fallout_pareto.note} testId="analytics-fallout-pareto">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={a.fallout_pareto.data} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="code" stroke={AXIS} tick={{ fontSize: 8 }} interval={0} tickFormatter={(v) => v.split(" ")[0]} />
              <YAxis stroke={AXIS} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={TT_STYLE} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="volume" name="Volume" fill="#2AA7A1" radius={[4, 4, 0, 0]} barSize={26} />
              <Bar dataKey="aged_over_10d" name="Aged >10d" fill="#D39B2A" radius={[4, 4, 0, 0]} barSize={26} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title={a.high_risk_segments.title} className="lg:col-span-1" testId="analytics-risk-segments" right={<DemoBadge />}>
          <div className="space-y-2">
            {a.high_risk_segments.data.map((s) => (
              <div key={s.segment} className="flex items-center justify-between gap-2 rounded-lg bg-[--shell-2] border border-[--divider] px-3 py-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-[--tx-1] leading-snug">{s.segment}</div>
                  <div className="text-[10px] text-[--tx-3]">{s.monthly_orders.toLocaleString()} orders/mo</div>
                </div>
                <span className="font-display text-sm font-bold text-[--danger] tabular-nums shrink-0">{s.rate}%</span>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[--tx-3] mt-2.5 leading-relaxed">{a.high_risk_segments.note}</p>
        </Panel>

        <Panel title={a.leading_indicators.title} className="lg:col-span-1" testId="analytics-leading-indicators">
          <div className="space-y-2">
            {a.leading_indicators.data.map((li) => (
              <div key={li.indicator} className="rounded-lg bg-[--shell-2] border border-[--divider] px-3 py-2">
                <div className="text-xs font-medium text-[--tx-1]">{li.indicator}</div>
                <div className="flex items-center gap-2 mt-1 text-[10px]">
                  <span className="text-[--tx-3]">Base: <span className="text-[--tx-2] font-mono">{li.baseline}</span></span>
                  <span className="text-[--green] font-mono">→ {li.target}</span>
                  <span className="ml-auto font-mono text-[--ai]">{li.pilot}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[--tx-3] mt-2.5 leading-relaxed">{a.leading_indicators.note}</p>
        </Panel>

        <Panel title={a.validation_fields.title} className="lg:col-span-1" testId="analytics-validation-fields"
          right={<StatusChip tone="risk">Demo → Validated</StatusChip>}>
          <div className="space-y-2">
            {a.validation_fields.items.map((f) => (
              <div key={f.field} className="rounded-lg bg-[--shell-2] border border-[--divider] px-3 py-2">
                <div className="text-xs font-medium text-[--tx-1]">{f.field}</div>
                <div className="text-[10px] text-[--tx-3] mt-0.5">Unblocks: {f.unblocks}</div>
                <StatusChip tone="ai" className="mt-1 !text-[9px]">{f.status}</StatusChip>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title={a.market_context.title} className="mt-4" testId="analytics-market-context" right={<StatusChip tone="neutral">External context</StatusChip>}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {a.market_context.items.map((m) => (
            <div key={m.label} className="rounded-lg bg-[--shell-2] border border-[--divider] p-3 text-center">
              <div className="font-display text-xl font-bold text-[--tx-1]">{m.stat}</div>
              <div className="text-[10px] text-[--tx-3] leading-snug mt-1">{m.label}</div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[--tx-3] mt-3 leading-relaxed">{a.market_context.note}</p>
      </Panel>
    </div>
  );
}
